import React, { useState } from 'react'
import { ContentWrapper, Value, Item, ItemActions } from '../styles'
import { parseISO } from 'date-fns'
import { safeFormat } from '@/v2/utils/convert'
import { useTheme } from '@mui/material/styles'
import MoreIcon from '@mui/icons-material/MoreVert'
import Typography from '@/v2/components/common/Typography'
import IconMenu from '@/v2/components/common/IconMenu'
import Button from '@/v2/components/common/Button'
import useClaimOptions from './useClaimOptions'
import useModals from '@/v2/hooks/useModals'
import Dialog from '@mui/material/Dialog'
import { Grid } from '@mui/material'
import {
  useRemoveClaim,
  useRecreateClaim,
} from '@/v2/hooks/insurance/useClaims'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'

const ClaimInfo = ({ claim, patient }) => {
  const { goTo } = useModals()
  const theme = useTheme()
  const { createdAt, updatedAt, insurance, status, subscriber } = claim
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false)
  const [openRecreateConfirmation, setOpenRecreateConfirmation] = useState(
    false
  )
  const { removeClaim } = useRemoveClaim(insurance?.id, claim.id, patient.id)
  const { recreateClaim } = useRecreateClaim(
    insurance?.id,
    claim.id,
    patient.id
  )
  const { onOpenSnackbar } = useSnackbarContext()

  const handleConfirmDelete = async () => {
    const { error } = await removeClaim()
    if (!error) {
      onOpenSnackbar('Claim removed', 'success')
    }
    setOpenDeleteConfirmation(false)
  }

  const handleConfirmRecreate = async () => {
    const result = await recreateClaim()
    setOpenRecreateConfirmation(false)

    if (!result.error) {
      onOpenSnackbar('Claim recreated successfully', 'success')
      const newClaimStatus = result.status
      if (
        newClaimStatus === 'pending' ||
        newClaimStatus === 'invalidated' ||
        newClaimStatus === 'answered'
      ) {
        goTo(`/v2/patient/${patient.id}/claim/edit`, {
          modalType: 'edit',
          patientId: patient.id,
          isEdit: true,
          insuranceId: result.insurance?.id,
          claim: result,
          patient,
        })
      } else if (
        newClaimStatus === 'validated' ||
        newClaimStatus === 'submitted' ||
        newClaimStatus === 'approved' ||
        newClaimStatus === 'cancelled' ||
        newClaimStatus === 'failed' ||
        newClaimStatus === 'denied'
      ) {
        goTo(`/v2/patient/${patient.id}/claim/view`, {
          modalType: 'view',
          patientId: patient.id,
          claimId: result.id,
          insuranceId: result.insurance?.id,
          claim: result,
        })
      }
    }
  }

  const { options, statusColors, outlinedStatus, getLabel } = useClaimOptions(
    status,
    claim.id,
    patient.id,
    insurance?.id,
    claim,
    patient,
    setOpenDeleteConfirmation,
    setOpenRecreateConfirmation
  )

  const handleStatusClick = status => {
    if (status === 'pending') {
      goTo(`v2/patient/${patient.id}/claim/edit`, {
        modalType: 'edit',
        patientId: patient.id,
        isEdit: true,
        insuranceId: insurance?.id,
        claim,
        patient,
      })
    }

    if (status === 'invalidated' || status === 'answered') {
      goTo(`v2/patient/${patient.id}/claim/edit`, {
        modalType: 'edit',
        patientId: patient.id,
        isEdit: true,
        insuranceId: insurance?.id,
        claim,
        patient,
      })
      // Scroll to history section after a short delay to ensure the modal is rendered
      setTimeout(() => {
        const historySection = document.getElementById('history-section')
        if (historySection) {
          historySection.scrollIntoView({ behavior: 'smooth' })
        }
      }, 100)
    }

    if (
      status === 'validated' ||
      status === 'submitted' ||
      status === 'approved' ||
      status === 'denied' ||
      status === 'cancelled' ||
      status === 'failed'
    ) {
      goTo(`v2/patient/${patient.id}/claim/view`, {
        modalType: 'view',
        patientId: patient.id,
        claimId: claim.id,
        insuranceId: insurance?.id,
        claim,
      })
    }
  }

  return (
    <Item theme={theme.palette}>
      <ContentWrapper>
        <Value>
          <Typography fontSize="14px" sx={{ mb: '2px' }}>
            Created
          </Typography>
          <Typography bold fontSize="14px" sx={{ mb: '2px' }}>
            {safeFormat(parseISO(createdAt), 'MMMM dd, yyyy HH:mm')}
          </Typography>
        </Value>
        <Value>
          <Typography fontSize="14px" sx={{ mb: '2px' }}>
            Subscriber
          </Typography>
          <Typography bold fontSize="14px" sx={{ mb: '2px' }}>
            {subscriber?.firstName || subscriber?.name || '-'}{' '}
            {subscriber?.lastName || ''}
          </Typography>
        </Value>
        <Value>
          <Typography fontSize="14px" sx={{ mb: '2px' }}>
            Insurance
          </Typography>
          <Typography bold fontSize="14px">
            {insurance?.payer?.name}
          </Typography>
        </Value>
        <Value>
          <Typography fontSize="14px" sx={{ mb: '2px' }}>
            Last Edit
          </Typography>
          <Typography bold fontSize="14px" sx={{ mb: '2px' }}>
            {safeFormat(parseISO(updatedAt), 'MMMM dd, yyyy HH:mm')}
          </Typography>
        </Value>
      </ContentWrapper>
      <ItemActions>
        <Button
          variant={outlinedStatus.includes(status) ? 'outlined' : 'contained'}
          size="small"
          sx={{
            background: outlinedStatus.includes(status)
              ? 'transparent'
              : statusColors[status],
            color: outlinedStatus.includes(status)
              ? statusColors[status]
              : theme.palette.background.default,
            borderColor: outlinedStatus.includes(status)
              ? statusColors[status]
              : 'transparent',
            width: 'auto',
            minWidth: '70px',
            height: 'auto',
            borderRadius: '50px',
            fontSize: '12px',
          }}
          onClick={() => handleStatusClick(status)}
        >
          {getLabel(status)}
        </Button>
        <IconMenu
          options={options}
          icon={<MoreIcon fontSize="small" />}
          aria-label="More"
          title="More"
        />
      </ItemActions>

      <Dialog
        open={openDeleteConfirmation}
        onClose={() => setOpenDeleteConfirmation(false)}
      >
        <div style={{ padding: '38px 30px 23px 30px', textAlign: 'center' }}>
          <Typography variant="h3" sx={{ marginBottom: '24px' }}>
            Are you sure you want to remove this claim?
          </Typography>
          <Typography
            variant="body2"
            sx={{ marginBottom: '24px', color: 'text.secondary' }}
          >
            This action cannot be undone. The claim will be permanently removed.
          </Typography>
          <Grid
            display="flex"
            width={'100%'}
            justifyContent={'flex-end'}
            gap={1}
          >
            <Button
              variant="text"
              color="secondary"
              onClick={() => setOpenDeleteConfirmation(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDelete}
              sx={{
                color: '#fff',
              }}
            >
              Remove
            </Button>
          </Grid>
        </div>
      </Dialog>

      <Dialog
        open={openRecreateConfirmation}
        onClose={() => setOpenRecreateConfirmation(false)}
      >
        <div style={{ padding: '38px 30px 23px 30px' }}>
          <Typography
            variant="h3"
            sx={{ marginBottom: '24px', textAlign: 'center', fontSize: '16px' }}
          >
            Recreate Claim
          </Typography>
          <Typography
            variant="body2"
            sx={{
              marginBottom: '14px',
              color: 'text.secondary',
              lineHeight: '20px',
              fontSize: '14px',
            }}
          >
            A new claim will be created with the exact same data (Patient,
            Providers, Items, and Attachments) from{' '}
            <strong>Claim #{claim.id}</strong>.
          </Typography>
          <Typography
            variant="body2"
            sx={{
              marginBottom: '24px',
              color: 'text.secondary',
              lineHeight: '20px',
              fontSize: '14px',
            }}
          >
            The new claim will be in Pending status, so you can make any changes
            requested by the insurance plan before resubmitting.
          </Typography>
          <Grid
            display="flex"
            width={'100%'}
            justifyContent={'flex-end'}
            gap={1}
          >
            <Button
              variant="text"
              color="secondary"
              onClick={() => setOpenRecreateConfirmation(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleConfirmRecreate}
            >
              Recreate Claim
            </Button>
          </Grid>
        </div>
      </Dialog>
    </Item>
  )
}

export default ClaimInfo
